import React, { useEffect, useState, useRef, useMemo } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getProducts, createProduct, updateProduct, deleteProduct, downloadProductTemplate, uploadProductsCSV, getSections, getCategoriesPublic, getMasterCategoriesPublic } from '../../services/api';
import { Plus, Search, Edit, Trash2, X, DollarSign, Upload, Download, FileSpreadsheet, Percent, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';
import ImageUpload from '../../components/ImageUpload';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['blockquote'],
    [{ 'align': [] }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'blockquote', 'align', 'link',
];

const Productos = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [globalCategories, setGlobalCategories] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    brand: '',
    category: '',
    category_id: '',
    subcategory_id: '',
    purchase_price: '',
    retail_price: '',
    compare_at_price: '',
    wholesale_enabled: false,
    wholesale_tier1_qty: '',
    wholesale_tier1_price: '',
    wholesale_tier2_qty: '',
    wholesale_tier2_price: '',
    wholesale_tier3_qty: '',
    wholesale_tier3_price: '',
    stock_min: 0,
    barcode: '',
    description_short: '',
    description_full: '',
    image_url: '',
    thumbnail_url: '',
    // SAT Configuration
    sat_code: '01010101',
    sat_unit: 'H87',
    sat_unit_name: 'Pieza',
    // Tax Configuration
    iva_rate: '0.16',
    iva_exempt: false,
    tax_object: '02',
    ieps_rate: '0',
    ieps_exempt: true,
    // Marketplace visibility
    hidden_from_marketplace: false,
    // Master category
    master_category_id: '',
    master_category_name: '',
  });

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadGlobalCategories();
    loadMasterCategories();
  }, [user]);

  const loadGlobalCategories = async () => {
    try {
      const response = await getCategoriesPublic();
      setGlobalCategories(response.data);
    } catch (error) {
      console.error('Error loading global categories:', error);
    }
  };

  const loadMasterCategories = async () => {
    try {
      const response = await getMasterCategoriesPublic();
      setMasterCategories(response.data);
    } catch (error) {
      console.error('Error loading master categories:', error);
    }
  };

  const loadCategories = async () => {
    if (!user?.business_id) return;
    try {
      const response = await getSections({ business_id: user.business_id });
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading subcategories:', error);
    }
  };

  const loadProducts = async () => {
    if (!user?.business_id) return;

    try {
      const response = await getProducts({ business_id: user.business_id, limit: 100 });
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const wholesaleTiers = [];
      
      if (formData.wholesale_enabled) {
        if (formData.wholesale_tier1_qty && formData.wholesale_tier1_price) {
          wholesaleTiers.push({
            min_quantity: parseInt(formData.wholesale_tier1_qty),
            unit_price: parseFloat(formData.wholesale_tier1_price),
            conditions: `Compra mínima: ${formData.wholesale_tier1_qty} unidades`
          });
        }
        if (formData.wholesale_tier2_qty && formData.wholesale_tier2_price) {
          wholesaleTiers.push({
            min_quantity: parseInt(formData.wholesale_tier2_qty),
            unit_price: parseFloat(formData.wholesale_tier2_price),
            conditions: `Compra mínima: ${formData.wholesale_tier2_qty} unidades`
          });
        }
        if (formData.wholesale_tier3_qty && formData.wholesale_tier3_price) {
          wholesaleTiers.push({
            min_quantity: parseInt(formData.wholesale_tier3_qty),
            unit_price: parseFloat(formData.wholesale_tier3_price),
            conditions: `Compra mínima: ${formData.wholesale_tier3_qty} unidades`
          });
        }
      }

      // Resolve category and subcategory names from IDs
      const selectedCat = globalCategories.find(c => c.id === formData.category_id);
      const selectedSubcat = categories.find(c => c.id === formData.subcategory_id);
      const productData = {
        sku: formData.sku,
        name: formData.name,
        brand: formData.brand,
        category: selectedCat ? selectedCat.name : formData.category,
        category_id: formData.category_id || undefined,
        subcategory_id: formData.subcategory_id || undefined,
        subcategory: selectedSubcat ? selectedSubcat.name : undefined,
        barcode: formData.barcode,
        description_short: formData.description_short,
        description_full: formData.description_full || undefined,
        image_url: formData.image_url,
        thumbnail_url: formData.thumbnail_url,
        stock_min: parseInt(formData.stock_min) || 0,
        business_id: user.business_id,
        // SAT Configuration
        sat_code: formData.sat_code || '01010101',
        sat_unit: formData.sat_unit || 'H87',
        sat_unit_name: formData.sat_unit_name || 'Pieza',
        // Tax Configuration
        tax_config: {
          iva_rate: parseFloat(formData.iva_rate) || 0.16,
          iva_exempt: formData.iva_exempt || false,
          tax_object: formData.tax_object || '02',
          ieps_rate: parseFloat(formData.ieps_rate) || 0,
          ieps_exempt: formData.ieps_exempt || true,
          retention_iva_rate: 0,
          retention_isr_rate: 0,
        },
        pricing: {
          purchase_price: parseFloat(formData.purchase_price) || 0,
          retail_price: parseFloat(formData.retail_price),
          compare_at_price: formData.compare_at_price ? parseFloat(formData.compare_at_price) : null,
          wholesale_enabled: formData.wholesale_enabled,
          wholesale_tiers: wholesaleTiers,
        },
        hidden_from_marketplace: formData.hidden_from_marketplace,
        master_category_id: formData.master_category_id || undefined,
        master_category_name: formData.master_category_name || undefined,
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, productData);
        toast.success('Producto actualizado');
      } else {
        await createProduct(productData);
        toast.success('Producto creado');
      }

      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(getErrorMessage(error, 'Error al guardar producto'));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const tiers = product.pricing?.wholesale_tiers || [];
    const taxConfig = product.tax_config || {};
    setFormData({
      sku: product.sku,
      name: product.name,
      brand: product.brand || '',
      category: product.category || '',
      category_id: product.category_id || '',
      subcategory_id: product.subcategory_id || '',
      purchase_price: product.pricing?.purchase_price || '',
      retail_price: product.pricing?.retail_price || '',
      compare_at_price: product.pricing?.compare_at_price || '',
      wholesale_enabled: product.pricing?.wholesale_enabled || false,
      wholesale_tier1_qty: tiers[0]?.min_quantity || '',
      wholesale_tier1_price: tiers[0]?.unit_price || '',
      wholesale_tier2_qty: tiers[1]?.min_quantity || '',
      wholesale_tier2_price: tiers[1]?.unit_price || '',
      wholesale_tier3_qty: tiers[2]?.min_quantity || '',
      wholesale_tier3_price: tiers[2]?.unit_price || '',
      stock_min: product.stock_min || 0,
      barcode: product.barcode || '',
      description_short: product.description_short || '',
      description_full: product.description_full || '',
      image_url: product.image_url || '',
      thumbnail_url: product.thumbnail_url || '',
      // SAT Configuration
      sat_code: product.sat_code || '01010101',
      sat_unit: product.sat_unit || 'H87',
      sat_unit_name: product.sat_unit_name || 'Pieza',
      // Tax Configuration
      iva_rate: taxConfig.iva_rate?.toString() || '0.16',
      iva_exempt: taxConfig.iva_exempt || false,
      tax_object: taxConfig.tax_object || '02',
      ieps_rate: taxConfig.ieps_rate?.toString() || '0',
      ieps_exempt: taxConfig.ieps_exempt !== false,
      hidden_from_marketplace: product.hidden_from_marketplace || false,
      master_category_id: product.master_category_id || '',
      master_category_name: product.master_category_name || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      await deleteProduct(productId);
      toast.success('Producto eliminado');
      loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Error al eliminar producto');
    }
  };

  const resetForm = () => {
    setFormData({
      sku: '',
      name: '',
      brand: '',
      category: '',
      category_id: '',
      subcategory_id: '',
      purchase_price: '',
      retail_price: '',
      compare_at_price: '',
      wholesale_enabled: false,
      wholesale_tier1_qty: '',
      wholesale_tier1_price: '',
      wholesale_tier2_qty: '',
      wholesale_tier2_price: '',
      wholesale_tier3_qty: '',
      wholesale_tier3_price: '',
      stock_min: 0,
      barcode: '',
      description_short: '',
      description_full: '',
      image_url: '',
      thumbnail_url: '',
      // SAT Configuration
      sat_code: '01010101',
      sat_unit: 'H87',
      sat_unit_name: 'Pieza',
      // Tax Configuration
      iva_rate: '0.16',
      iva_exempt: false,
      tax_object: '02',
      ieps_rate: '0',
      ieps_exempt: true,
      hidden_from_marketplace: false,
      master_category_id: '',
      master_category_name: '',
    });
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadProductTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_productos.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Plantilla descargada');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Error al descargar plantilla');
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Por favor selecciona un archivo CSV');
      return;
    }

    setUploading(true);
    setUploadResults(null);

    try {
      const response = await uploadProductsCSV(user.business_id, file);
      setUploadResults(response.data);
      
      const total = response.data.created + response.data.updated;
      if (total > 0) {
        toast.success(`${response.data.created} creados, ${response.data.updated} actualizados`);
        loadProducts();
      }
      
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} errores encontrados`);
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error(getErrorMessage(error, 'Error al subir archivo'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const calculateMargin = (purchase, retail) => {
    if (!purchase || !retail) return 0;
    return ((retail - purchase) / retail * 100).toFixed(1);
  };

  const filteredProducts = products.filter((product) =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.barcode && product.barcode.includes(searchTerm))
  );

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Productos</h1>
            <p className="text-slate-600 mt-2">Gestiona tu catálogo de productos</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
              data-testid="download-product-template-btn"
            >
              <Download className="w-4 h-4" />
              Plantilla CSV
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
              data-testid="upload-products-csv-btn"
            >
              <Upload className="w-4 h-4" />
              Subir CSV
            </button>
            <button
              onClick={() => {
                resetForm();
                setEditingProduct(null);
                setShowModal(true);
              }}
              data-testid="add-product-button"
              className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
            >
              <Plus className="w-5 h-5" />
              Nuevo Producto
            </button>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar por nombre, SKU o código de barras..."
            data-testid="search-products-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Imagen</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Producto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Marca</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Compra</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Venta</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Margen</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-slate-500">
                    Cargando productos...
                  </td>
                </tr>
              ) : filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-slate-500">
                    No se encontraron productos
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => {
                  const margin = calculateMargin(
                    product.pricing?.purchase_price,
                    product.pricing?.retail_price
                  );
                  return (
                    <tr key={product.id} data-testid={`product-row-${product.id}`} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        {product.image_url ? (
                          <img src={product.thumbnail_url || product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                        ) : (
                          <div className="w-12 h-12 bg-slate-200 rounded flex items-center justify-center">
                            <span className="text-slate-400 text-xs">Sin img</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-slate-900">{product.sku}</td>
                      <td className="px-6 py-4 text-sm font-medium text-slate-900">
                        {product.name}
                        {product.pricing?.wholesale_enabled && (
                          <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Mayoreo</span>
                        )}
                        {product.hidden_from_marketplace && (
                          <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full flex items-center gap-1 inline-flex"><EyeOff className="w-3 h-3" />Oculto</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{product.brand || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                        ${(product.pricing?.purchase_price || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-slate-900">
                        ${(product.pricing?.retail_price || 0).toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`font-bold ${margin > 30 ? 'text-green-600' : margin > 15 ? 'text-orange-600' : 'text-red-600'}`}>
                          {margin}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(product)}
                          data-testid={`edit-product-${product.id}`}
                          className="text-teal-700 hover:text-teal-900 mr-3"
                        >
                          <Edit className="w-4 h-4 inline" />
                        </button>
                        <button
                          onClick={() => handleDelete(product.id)}
                          data-testid={`delete-product-${product.id}`}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full my-8">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="text-2xl font-bold text-slate-900">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingProduct(null);
                  resetForm();
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="col-span-1">
                  <ImageUpload
                    value={formData.image_url}
                    onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                    onThumbnailChange={(url) => setFormData(prev => ({ ...prev, thumbnail_url: url }))}
                    type="product"
                    label="Imagen del Producto"
                  />
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">SKU *</label>
                    <input
                      type="text"
                      required
                      data-testid="product-sku-input"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Código de Barras</label>
                    <input
                      type="text"
                      data-testid="product-barcode-input"
                      value={formData.barcode}
                      onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre del Producto *</label>
                <input
                  type="text"
                  required
                  data-testid="product-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Marca</label>
                  <input
                    type="text"
                    data-testid="product-brand-input"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Categoria Maestra</label>
                  <select
                    value={formData.master_category_id}
                    onChange={(e) => {
                      const selected = masterCategories.find(c => c.id === e.target.value);
                      setFormData({ ...formData, master_category_id: e.target.value, master_category_name: selected ? selected.name : '' });
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Seleccionar categoria maestra...</option>
                    {masterCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon || '📂'} {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Categoria</label>
                  <select
                    data-testid="product-category-input"
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value, subcategory_id: '' })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Seleccionar categoria...</option>
                    {globalCategories
                      .filter((cat) => !formData.master_category_id || cat.master_category_id === formData.master_category_id)
                      .map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Seccion</label>
                  <select
                    data-testid="product-subcategory-input"
                    value={formData.subcategory_id}
                    onChange={(e) => setFormData({ ...formData, subcategory_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Seleccionar seccion...</option>
                    {categories
                      .filter((cat) => !formData.category_id || !cat.parent_category_id || cat.parent_category_id === formData.category_id)
                      .map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.icon || '📁'} {cat.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <h3 className="font-bold text-slate-900 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-blue-700" />
                  Precios y Utilidad
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Precio de Compra *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      data-testid="product-purchase-price-input"
                      value={formData.purchase_price}
                      onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Precio de Venta *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      data-testid="product-retail-price-input"
                      value={formData.retail_price}
                      onChange={(e) => setFormData({ ...formData, retail_price: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Precio Anterior</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.compare_at_price}
                      onChange={(e) => setFormData({ ...formData, compare_at_price: e.target.value })}
                      placeholder="Opcional"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    {formData.compare_at_price && parseFloat(formData.compare_at_price) > parseFloat(formData.retail_price || 0) && (
                      <span className="text-xs text-red-500 font-medium mt-1 block">
                        -{Math.round((1 - parseFloat(formData.retail_price || 0) / parseFloat(formData.compare_at_price)) * 100)}% dto
                      </span>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Margen de Utilidad</label>
                    <div className="px-3 py-2 bg-white border border-slate-200 rounded-lg">
                      <span className={`text-lg font-bold ${
                        calculateMargin(formData.purchase_price, formData.retail_price) > 30
                          ? 'text-green-600'
                          : calculateMargin(formData.purchase_price, formData.retail_price) > 15
                          ? 'text-orange-600'
                          : 'text-red-600'
                      }`}>
                        {calculateMargin(formData.purchase_price, formData.retail_price)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.wholesale_enabled}
                    onChange={(e) => setFormData({ ...formData, wholesale_enabled: e.target.checked })}
                    className="w-5 h-5 text-purple-700 rounded focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="font-bold text-slate-900">Habilitar Precio de Mayoreo</span>
                </label>

                {formData.wholesale_enabled && (
                  <div className="space-y-3 mt-4">
                    <p className="text-sm text-slate-600">Configura hasta 3 niveles de precio por volumen:</p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Nivel 1 - Cantidad Mínima</label>
                        <input
                          type="number"
                          placeholder="Ej: 12"
                          value={formData.wholesale_tier1_qty}
                          onChange={(e) => setFormData({ ...formData, wholesale_tier1_qty: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Precio por Unidad</label>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Ej: 12.50"
                          value={formData.wholesale_tier1_price}
                          onChange={(e) => setFormData({ ...formData, wholesale_tier1_price: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Nivel 2 - Cantidad Mínima</label>
                        <input
                          type="number"
                          placeholder="Ej: 24"
                          value={formData.wholesale_tier2_qty}
                          onChange={(e) => setFormData({ ...formData, wholesale_tier2_qty: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Precio por Unidad</label>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Ej: 11.50"
                          value={formData.wholesale_tier2_price}
                          onChange={(e) => setFormData({ ...formData, wholesale_tier2_price: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Nivel 3 - Cantidad Mínima</label>
                        <input
                          type="number"
                          placeholder="Ej: 48"
                          value={formData.wholesale_tier3_qty}
                          onChange={(e) => setFormData({ ...formData, wholesale_tier3_qty: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Precio por Unidad</label>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Ej: 10.50"
                          value={formData.wholesale_tier3_price}
                          onChange={(e) => setFormData({ ...formData, wholesale_tier3_price: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.hidden_from_marketplace}
                    onChange={(e) => setFormData({ ...formData, hidden_from_marketplace: e.target.checked })}
                    className="w-5 h-5 text-orange-600 rounded focus:ring-2 focus:ring-orange-500"
                  />
                  <EyeOff className="w-5 h-5 text-orange-600" />
                  <span className="font-bold text-slate-900">Ocultar del Marketplace</span>
                </label>
                {formData.hidden_from_marketplace && (
                  <p className="text-sm text-orange-700 ml-12">
                    Este producto no aparecera en busquedas ni en la tienda del marketplace
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Stock Mínimo</label>
                <input
                  type="number"
                  data-testid="product-stockmin-input"
                  value={formData.stock_min}
                  onChange={(e) => setFormData({ ...formData, stock_min: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción Corta</label>
                <textarea
                  rows="3"
                  data-testid="product-description-input"
                  value={formData.description_short}
                  onChange={(e) => setFormData({ ...formData, description_short: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción Detallada</label>
                <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    value={formData.description_full}
                    onChange={(value) => setFormData(prev => ({ ...prev, description_full: value }))}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Escribe una descripción detallada del producto con formato..."
                    className="[&_.ql-editor]:min-h-[120px]"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Puedes usar negritas, listas, encabezados y más. Se mostrará con formato en la página del producto.</p>
              </div>

              {/* Configuración Fiscal */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-blue-600" />
                  <span className="font-bold text-slate-900">Configuración Fiscal (SAT)</span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Tasa de IVA</label>
                    <select
                      value={formData.iva_rate}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        iva_rate: e.target.value,
                        iva_exempt: e.target.value === '0'
                      })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      data-testid="product-iva-select"
                    >
                      <option value="0.16">16% (General)</option>
                      <option value="0.08">8% (Frontera)</option>
                      <option value="0">0% (Exento)</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      {formData.iva_rate === '0.08' ? 'Zona fronteriza norte' : formData.iva_rate === '0' ? 'Productos exentos' : 'Tasa general México'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Objeto de Impuesto</label>
                    <select
                      value={formData.tax_object}
                      onChange={(e) => setFormData({ ...formData, tax_object: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="02">02 - Sí objeto de impuesto</option>
                      <option value="01">01 - No objeto de impuesto</option>
                      <option value="03">03 - Sí objeto, sin desglose</option>
                      <option value="04">04 - Sí objeto, no causa</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Tasa IEPS</label>
                    <select
                      value={formData.ieps_rate}
                      onChange={(e) => setFormData({ 
                        ...formData, 
                        ieps_rate: e.target.value,
                        ieps_exempt: e.target.value === '0'
                      })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="0">No aplica IEPS</option>
                      <option value="0.265">26.5% (Bebidas alcohólicas 14-20°)</option>
                      <option value="0.53">53% (Bebidas alcohólicas +20°)</option>
                      <option value="0.30">30% (Tabaco)</option>
                      <option value="0.08">8% (Comida chatarra)</option>
                      <option value="0.25">25% (Bebidas energizantes)</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">Impuesto especial a ciertos productos</p>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Clave SAT</label>
                    <input
                      type="text"
                      value={formData.sat_code}
                      onChange={(e) => setFormData({ ...formData, sat_code: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder="01010101"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      <a href="http://pys.sat.gob.mx/PyS/catPyS.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Buscar clave →
                      </a>
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Clave Unidad SAT</label>
                    <select
                      value={formData.sat_unit}
                      onChange={(e) => {
                        const unitNames = {
                          'H87': 'Pieza',
                          'E48': 'Servicio',
                          'KGM': 'Kilogramo',
                          'LTR': 'Litro',
                          'MTR': 'Metro',
                          'XBX': 'Caja',
                          'XPK': 'Paquete'
                        };
                        setFormData({ 
                          ...formData, 
                          sat_unit: e.target.value,
                          sat_unit_name: unitNames[e.target.value] || 'Pieza'
                        });
                      }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="H87">H87 - Pieza</option>
                      <option value="E48">E48 - Servicio</option>
                      <option value="KGM">KGM - Kilogramo</option>
                      <option value="LTR">LTR - Litro</option>
                      <option value="MTR">MTR - Metro</option>
                      <option value="XBX">XBX - Caja</option>
                      <option value="XPK">XPK - Paquete</option>
                    </select>
                  </div>
                </div>
                
                <p className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                  💡 <strong>Nota:</strong> La configuración fiscal se usará automáticamente al facturar ventas de este producto.
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-200 sticky bottom-0 bg-white">
                <button
                  type="submit"
                  data-testid="product-form-submit"
                  className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors"
                >
                  {editingProduct ? 'Actualizar Producto' : 'Crear Producto'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingProduct(null);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload CSV Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-2xl font-bold text-slate-900">Importar Productos CSV</h2>
              <p className="text-sm text-slate-600 mt-1">
                Carga múltiples productos de una vez
              </p>
            </div>
            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  Formato del archivo CSV
                </h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>nombre</strong>: Nombre del producto (requerido)</li>
                  <li>• <strong>sku</strong>: Código único (requerido)</li>
                  <li>• <strong>precio_venta</strong>: Precio al público (requerido)</li>
                  <li>• <strong>precio_compra</strong>: Costo del producto</li>
                  <li>• <strong>codigo_barras, descripcion, categoria, stock_minimo, unidad</strong></li>
                </ul>
                <button
                  onClick={handleDownloadTemplate}
                  className="mt-3 text-sm text-blue-700 underline hover:text-blue-900"
                >
                  Descargar plantilla con ejemplos
                </button>
              </div>

              {/* File Input */}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  ref={fileInputRef}
                  className="hidden"
                  id="csv-products-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="csv-products-upload"
                  className={`cursor-pointer ${uploading ? 'opacity-50' : ''}`}
                >
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600">
                    {uploading ? 'Procesando...' : 'Haz clic para seleccionar archivo CSV'}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Los productos existentes (mismo SKU) serán actualizados
                  </p>
                </label>
              </div>

              {/* Upload Results */}
              {uploadResults && (
                <div className="space-y-3">
                  <div className="flex gap-4">
                    <div className="flex-1 bg-green-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">{uploadResults.created}</p>
                      <p className="text-sm text-green-600">Creados</p>
                    </div>
                    <div className="flex-1 bg-blue-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">{uploadResults.updated}</p>
                      <p className="text-sm text-blue-600">Actualizados</p>
                    </div>
                    <div className="flex-1 bg-red-50 p-3 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-700">{uploadResults.errors?.length || 0}</p>
                      <p className="text-sm text-red-600">Errores</p>
                    </div>
                  </div>
                  
                  {uploadResults.errors?.length > 0 && (
                    <div className="bg-red-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                      <p className="text-sm font-medium text-red-700 mb-1">Errores:</p>
                      {uploadResults.errors.map((err, idx) => (
                        <p key={idx} className="text-xs text-red-600">{err}</p>
                      ))}
                    </div>
                  )}

                  {uploadResults.products?.length > 0 && (
                    <div className="bg-slate-50 p-3 rounded-lg max-h-32 overflow-y-auto">
                      <p className="text-sm font-medium text-slate-700 mb-1">Productos procesados:</p>
                      {uploadResults.products.slice(0, 10).map((prod, idx) => (
                        <p key={idx} className="text-xs text-slate-600">
                          {prod.sku} - {prod.name} ({prod.action})
                        </p>
                      ))}
                      {uploadResults.products.length > 10 && (
                        <p className="text-xs text-slate-500 mt-1">
                          ...y {uploadResults.products.length - 10} más
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadResults(null);
                  }}
                  className="flex-1 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Productos;
